import type { RefObject } from "react";

/**
 * Enum for the QR scanner's camera lifecycle status.
 * @enum QRScannerStatus
 */
export enum QRScannerStatus {
  Requesting = "Requesting",
  Scanning = "Scanning",
  Error = "Error",
}

/**
 * Interface representing the parameters for the useQRScanner hook.
 * @interface IUseQRScannerParams
 * @property {boolean} active: Whether the camera should be running; acquires on true, releases on false.
 * @property {Function} onDetected: Called once with the decoded (trimmed) QR string on a successful scan.
 */
export interface IUseQRScannerParams {
  active: boolean;
  onDetected: (code: string) => void;
}

/**
 * Interface representing the return value of the useQRScanner hook.
 * @interface IUseQRScanner
 * @property {RefObject<HTMLVideoElement | null>} videoRef: Ref attached to the live camera video element.
 * @property {RefObject<HTMLCanvasElement | null>} canvasRef: Ref attached to the hidden decode canvas element.
 * @property {QRScannerStatus} status: Current camera lifecycle status.
 * @property {string} errorMessage: User-facing message shown when status is Error (empty otherwise).
 * @property {Function} stop: Stops the camera and cancels the decode loop.
 */
export interface IUseQRScanner {
  videoRef: RefObject<HTMLVideoElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  status: QRScannerStatus;
  errorMessage: string;
  stop: () => void;
}
